// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React from 'react';
import { Col } from 'antd/lib/grid';
import Icon, { StopOutlined, CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal';
import Button from 'antd/lib/button';
import Text from 'antd/lib/typography/Text';
import Dropdown from 'antd/lib/dropdown';
import GlobalHotKeys, { KeyMap } from 'utils/mousetrap-react';

import AnnotationMenuContainer from 'containers/annotation-page/top-bar/annotation-menu';
import { MainMenuIcon, UndoIcon, RedoIcon } from 'icons';
import {
    ActiveControl, CombinedState, ToolsBlockerState, Workspace,
} from 'reducers';
import CVATTooltip from 'components/common/cvat-tooltip';
import customizableComponents from 'components/customizable-components';
import { GetHistoryActionsChinese } from 'cvat-core/src/enums';
import { useSelector } from 'react-redux';

// saving是一个记录系统是否在保存中的状态，同时saving控制了一个元素的显示。
// TODO： 设计让saving有更多状态，只能把boolean改成number

export enum AnnotationSaveState {
    SAVING = 1, // sync
    NOT_SAVING = 0,
    ASYNC_SAVING = 2, // async
}

function isSyncSaving(annotationSaveState:AnnotationSaveState): boolean {
    if (annotationSaveState === AnnotationSaveState.SAVING) {
        return true;
    }
    return false;
}

/* function isAsyncSaving(annotationSaveState:AnnotationSaveState): boolean {
    if (annotationSaveState === AnnotationSaveState.ASYNC_SAVING) {
        return true;
    }
    return false;
} */

function isSaving(annotationSaveState:AnnotationSaveState): boolean {
    if (annotationSaveState === AnnotationSaveState.NOT_SAVING) {
        return false;
    }
    return true;
}

function isReviewing(workspace: string): boolean {
    return workspace === Workspace.REVIEW_WORKSPACE;
}

interface Props {
    saving: number;
    undoAction?: string;
    redoAction?: string;
    undoShortcut: string;
    redoShortcut: string;
    drawShortcut: string;
    switchToolsBlockerShortcut: string;
    toolsBlockerState: ToolsBlockerState;
    activeControl: ActiveControl;
    onSaveAnnotation(async: boolean): void;
    onUndoClick(): void;
    onRedoClick(): void;
    onFinishDraw(): void;
    onSwitchToolsBlockerState(): void;
}

function LeftGroup(props: Props): JSX.Element {
    const {
        saving,
        undoAction,
        redoAction,
        undoShortcut,
        redoShortcut,
        drawShortcut,
        switchToolsBlockerShortcut,
        activeControl,
        toolsBlockerState,
        onSaveAnnotation,
        onUndoClick,
        onRedoClick,
        onFinishDraw,
        onSwitchToolsBlockerState,
    } = props;

    const includesDoneButton = [
        ActiveControl.DRAW_POLYGON,
        ActiveControl.DRAW_POLYLINE,
        ActiveControl.DRAW_POINTS,
        ActiveControl.AI_TOOLS,
        ActiveControl.OPENCV_TOOLS,
    ].includes(activeControl);

    const includesToolsBlockerButton =
        [ActiveControl.OPENCV_TOOLS, ActiveControl.AI_TOOLS].includes(activeControl) && toolsBlockerState.buttonVisible;

    const shouldEnableToolsBlockerOnClick = [ActiveControl.OPENCV_TOOLS].includes(activeControl);
    const SaveButtonComponent = customizableComponents.SAVE_ANNOTATION_BUTTON;
    const workspace = useSelector((state: CombinedState) => state.annotation.workspace);

    // 定义保存快捷键
    const keyMap: KeyMap = {
        SAVE_ANNOTATIONS: {
            name: '保存标注',
            description: '保存当前标注状态',
            sequences: ['ctrl+s'],
            action: 'keydown',
            applicable: ['annotation', 'review']
        },
    };

    // 处理快捷键事件
    const handlers = {
        SAVE_ANNOTATIONS: (event: KeyboardEvent) => {
            event.preventDefault();
            if (!isSaving(saving) && !isReviewing(workspace)) {
                onSaveAnnotation(false);
            }
        },
    };

    return (
        <>
            <Modal className='cvat-saving-job-modal' title='将改动保存到服务器' visible={isSyncSaving(saving)} footer={[]} closable={false}>
                <Text>正在保存你的改动，请稍等...</Text>
                <LoadingOutlined />
            </Modal>
            <Col className='cvat-annotation-header-left-group'>
                <Dropdown overlay={<AnnotationMenuContainer />}>
                    <Button type='link' className='cvat-annotation-header-menu-button cvat-annotation-header-button'>
                        <Icon component={MainMenuIcon} />
                        菜单
                    </Button>
                </Dropdown>
                <SaveButtonComponent
                    isSaving={isSaving(saving)}
                    onClick={isSaving(saving) || isReviewing(workspace) ? undefined : onSaveAnnotation}
                    type='link'
                    className={isSaving(saving) || isReviewing(workspace) ?
                        'cvat-annotation-header-save-button cvat-annotation-disabled-header-button' :
                        'cvat-annotation-header-save-button cvat-annotation-header-button'}
                />
                <CVATTooltip overlay={`撤销：${GetHistoryActionsChinese(undoAction ?? '')} ${undoShortcut}`}>
                    <Button
                        style={{ pointerEvents: undoAction ? 'initial' : 'none', opacity: undoAction ? 1 : 0.5 }}
                        type='link'
                        className='cvat-annotation-header-undo-button cvat-annotation-header-button'
                        onClick={onUndoClick}
                    >
                        <Icon component={UndoIcon} />
                        <span>撤销</span>
                    </Button>
                </CVATTooltip>
                <CVATTooltip overlay={`回退：${GetHistoryActionsChinese(redoAction ?? '')} ${redoShortcut}`}>
                    <Button
                        style={{ pointerEvents: redoAction ? 'initial' : 'none', opacity: redoAction ? 1 : 0.5 }}
                        type='link'
                        className='cvat-annotation-header-redo-button cvat-annotation-header-button'
                        onClick={onRedoClick}
                    >
                        <Icon component={RedoIcon} />
                        回退
                    </Button>
                </CVATTooltip>
                {includesDoneButton ? (
                    <CVATTooltip overlay={`按 "${drawShortcut}" 完成`}>
                        <Button type='link' className='cvat-annotation-header-done-button cvat-annotation-header-button' onClick={onFinishDraw}>
                            <CheckCircleOutlined />
                            完成
                        </Button>
                    </CVATTooltip>
                ) : null}
                {includesToolsBlockerButton ? (
                    <CVATTooltip overlay={`按 "${switchToolsBlockerShortcut}" 延迟运行算法 `}>
                        <Button
                            type='link'
                            className={`cvat-annotation-header-block-tool-button cvat-annotation-header-button ${
                                toolsBlockerState.algorithmsLocked ? 'cvat-button-active' : ''
                            }`}
                            onClick={shouldEnableToolsBlockerOnClick ? onSwitchToolsBlockerState : undefined}
                        >
                            <StopOutlined />
                            阻塞
                        </Button>
                    </CVATTooltip>
                ) : null}
            </Col>
            <GlobalHotKeys keyMap={keyMap} handlers={handlers} />
        </>
    );
}

export default React.memo(LeftGroup);
